import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
    Calendar, Loader2, CheckCircle2, ArrowRight, Clock, Globe, RotateCcw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const TIMEZONES = [
    "America/Los_Angeles", "America/Denver", "America/Chicago", "America/New_York",
    "UTC", "Europe/London", "Europe/Berlin", "Asia/Dubai", "Asia/Kolkata",
    "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney",
];
const tzShort = (iso, tz) => {
    try {
        const p = new Intl.DateTimeFormat(undefined, { timeZone: tz, timeZoneName: "short" }).formatToParts(new Date(iso));
        return p.find((x) => x.type === "timeZoneName")?.value || tz;
    } catch { return tz; }
};
const fmtTime = (iso, tz) => new Intl.DateTimeFormat(undefined, { timeZone: tz, hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
const fmtDay = (iso, tz) => new Intl.DateTimeFormat(undefined, { timeZone: tz, weekday: "long", month: "short", day: "numeric" }).format(new Date(iso));

const Reschedule = () => {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);
    const [expired, setExpired] = useState(false);
    const [tz, setTz] = useState(() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; } });

    useEffect(() => {
        setLoading(true);
        api.get(`/reschedule/${token}`)
            .then(({ data }) => setData(data))
            .catch((err) => {
                if ([404, 410].includes(err?.response?.status)) setExpired(true);
                else toast.error(formatApiError(err));
            })
            .finally(() => setLoading(false));
    }, [token]);

    const groups = useMemo(() => {
        if (!data?.slots) return {};
        return data.slots.reduce((acc, iso) => {
            const day = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
            (acc[day] = acc[day] || []).push(iso);
            return acc;
        }, {});
    }, [data, tz]);

    const submit = async () => {
        if (!selected) return toast.error("Pick a new time first.");
        setSubmitting(true);
        try {
            const { data: r } = await api.post(`/reschedule/${token}`, { start_iso: selected });
            setDone(r.new_start_iso);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSubmitting(false); }
    };

    if (loading) return <div className="py-24 flex justify-center"><Loader2 size={20} className="animate-spin text-emerald-500" /></div>;
    if (expired) return (
        <div className="max-w-md mx-auto text-center py-24 px-6" data-testid="reschedule-expired">
            <RotateCcw size={26} className="mx-auto text-emerald-500" />
            <h1 className="mt-4 font-heading font-semibold text-2xl text-foreground">Link no longer valid</h1>
            <p className="mt-2 text-sm text-muted-foreground">Please contact the employer directly.</p>
        </div>
    );
    if (!data) return null;

    if (done) {
        return (
            <div className="max-w-md mx-auto text-center py-24 px-6" data-testid="reschedule-done">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:ring-emerald-500/30">
                    <CheckCircle2 size={24} className="text-emerald-600 dark:text-emerald-400" />
                </div>
                <h1 className="font-heading font-semibold text-2xl text-foreground">Rescheduled!</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                    Your new time: <strong className="text-foreground">{fmtDay(done, tz)} at {fmtTime(done, tz)} ({tzShort(done, tz)})</strong>. A confirmation email is on its way.
                </p>
            </div>
        );
    }

    return (
        <div data-testid="reschedule-page" className="max-w-4xl mx-auto px-6 py-12">
            <Helmet><title>Reschedule your interview · Simonara</title></Helmet>
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="overline">Reschedule</p>
                    <h1 className="mt-1 font-heading font-medium text-3xl text-foreground">Move your interview</h1>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        <span>Currently booked for</span>
                        <Badge variant="outline" className="text-xs" data-testid="reschedule-current">
                            <Clock size={11} className="inline mr-1" /> {fmtDay(data.current.start_iso, tz)}, {fmtTime(data.current.start_iso, tz)} ({tzShort(data.current.start_iso, tz)})
                        </Badge>
                    </div>
                </div>
                <div className="min-w-[240px]">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                        <Globe size={11} /> Timezone
                    </div>
                    <Select value={tz} onValueChange={setTz}>
                        <SelectTrigger data-testid="reschedule-tz" className="bg-background text-xs h-9">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="card-soft p-5">
                <p className="overline mb-3">Pick a new time</p>
                {Object.keys(groups).length === 0 ? (
                    <div className="text-sm text-muted-foreground">No open slots — please contact the employer.</div>
                ) : (
                    <div className="space-y-5 max-h-[520px] overflow-y-auto pr-2">
                        {Object.entries(groups).map(([day, slots]) => (
                            <div key={day}>
                                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                                    {fmtDay(slots[0], tz)}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {slots.map((iso) => {
                                        const active = selected === iso;
                                        return (
                                            <button
                                                key={iso}
                                                onClick={() => setSelected(iso)}
                                                data-testid={`reslot-${iso}`}
                                                className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${active ? "bg-emerald-600 text-white border-emerald-600" : "border-border bg-card text-foreground hover:border-emerald-500/40"}`}
                                            >
                                                {fmtTime(iso, tz)}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="mt-5 flex items-center justify-end gap-2">
                    <button
                        onClick={submit}
                        disabled={!selected || submitting}
                        data-testid="reschedule-submit"
                        className="btn-green disabled:opacity-60"
                    >
                        {submitting ? <><Loader2 size={13} className="mr-1.5 inline animate-spin" /> Saving…</> : <>Confirm new time <ArrowRight size={13} className="ml-1.5 inline" /></>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Reschedule;
